import type { AppContext, ViewCleanup } from "../app";
import {
  loadCountries,
  groupByRegion,
  acceptedNamesFor,
  type CountryMetadata,
} from "../data/countries";
import { loadState, saveState } from "../state/storage";
import { recordCorrect, recordWrong } from "../state/stats";
import { getArcadeBest, recordArcadeScore } from "../state/arcade";
import { pushLeaderboardBests } from "../state/sync";
import { startRound, drawNext, type RoundState } from "../lib/selection";
import { fuzzyMatch } from "../lib/fuzzy";
import { getCountriesForRegion } from "./region-picker";

const ESCAPE_PREVIEW_MS = 1200;

export const mountArcadeSurvival = async (
  root: HTMLElement,
  ctx: AppContext,
): Promise<ViewCleanup> => {
  const region = root.dataset.region;
  if (!region) {
    ctx.nav.goArcade();
    return () => {};
  }

  root.classList.add("view-arcade-run");
  ctx.globe.setHoverEnabled(false);
  ctx.globe.setClickHandler(null);

  const table = await loadCountries();
  const grouped = groupByRegion(table);
  let appState = loadState();
  const pool = getCountriesForRegion(region, grouped, appState);
  const poolIsos = pool.map((c) => c.iso);
  const tolerance = appState.settings.fuzzyMatchTolerance;

  root.innerHTML = `
    <button type="button" class="arcade-run-back" aria-label="Back">← Back</button>
    <div class="arcade-run-hud">
      <span class="arcade-run-streak">0</span> streak
    </div>
    <section class="arcade-run-panel">
      <p class="arcade-run-prompt">What country is this?</p>
      <form class="arcade-run-form">
        <input
          type="text"
          class="arcade-run-input"
          autocomplete="off"
          autocapitalize="off"
          autocorrect="off"
          spellcheck="false"
          placeholder="Type the country name"
          aria-label="Country name"
        />
        <button type="submit" class="arcade-run-submit">Submit</button>
      </form>
    </section>
    <section class="arcade-run-end" hidden>
      <h1 class="arcade-run-final-title">Run over</h1>
      <p class="arcade-run-final-detail"></p>
      <p class="arcade-run-final-score"></p>
      <p class="arcade-run-final-best"></p>
      <div class="arcade-run-actions">
        <button type="button" class="arcade-run-retry">Run it again</button>
        <button type="button" class="arcade-run-leaderboard">🏆 Leaderboard</button>
        <button type="button" class="arcade-run-exit">Back to Arcade</button>
      </div>
    </section>
  `;

  const backBtn = root.querySelector(".arcade-run-back") as HTMLButtonElement;
  const streakEl = root.querySelector(".arcade-run-streak") as HTMLSpanElement;
  const promptEl = root.querySelector(".arcade-run-prompt") as HTMLParagraphElement;
  const panel = root.querySelector(".arcade-run-panel") as HTMLElement;
  const form = root.querySelector(".arcade-run-form") as HTMLFormElement;
  const input = root.querySelector(".arcade-run-input") as HTMLInputElement;
  const endEl = root.querySelector(".arcade-run-end") as HTMLElement;
  const finalDetail = root.querySelector(".arcade-run-final-detail") as HTMLParagraphElement;
  const finalScore = root.querySelector(".arcade-run-final-score") as HTMLParagraphElement;
  const finalBest = root.querySelector(".arcade-run-final-best") as HTMLParagraphElement;
  const retryBtn = root.querySelector(".arcade-run-retry") as HTMLButtonElement;
  const leaderboardBtn = root.querySelector(".arcade-run-leaderboard") as HTMLButtonElement;
  const exitBtn = root.querySelector(".arcade-run-exit") as HTMLButtonElement;

  let current: CountryMetadata | null = null;
  let streak = 0;
  let destroyed = false;
  let ended = false;
  let previewTimer: number | null = null;
  let round: RoundState = startRound(poolIsos, Math.random);

  if (pool.length === 0) {
    promptEl.textContent = `"${region}" has no countries to play with.`;
    form.hidden = true;
    return () => {
      ctx.globe.setHoverEnabled(true);
      root.classList.remove("view-arcade-run");
    };
  }

  const nextQuestion = (): void => {
    if (destroyed || ended) return;
    const draw = drawNext(round, Math.random);
    round = draw.state;
    const pickedIso = draw.iso;
    if (!pickedIso) return;
    const picked = table[pickedIso];
    if (!picked) return;
    current = picked;
    input.value = "";
    input.disabled = false;
    input.focus();
    ctx.globe.setTargetIso(picked.iso);
    void ctx.globe.flyToIso(picked.iso, { durationMs: 600 });
  };

  const endRun = (lastWrong: CountryMetadata | null): void => {
    if (ended) return;
    ended = true;
    panel.hidden = true;
    endEl.hidden = false;
    finalDetail.textContent = lastWrong
      ? `You missed ${lastWrong.canonicalName}.`
      : "Session ended.";
    finalScore.textContent = `Longest streak: ${streak}`;
    const prevBest = getArcadeBest(appState, "survival", region);
    const result = recordArcadeScore(
      appState,
      "survival",
      region,
      streak,
      Date.now(),
    );
    appState = result.state;
    saveState(appState);
    if (result.isNewBest) {
      finalBest.innerHTML = `<strong>New best streak!</strong> (was ${prevBest ? prevBest.score : 0})`;
      void pushLeaderboardBests(appState);
    } else {
      finalBest.textContent = `Best streak: ${prevBest ? prevBest.score : 0}`;
    }
    // Leave the missed country highlighted while the end screen sits.
    if (previewTimer !== null) {
      window.clearTimeout(previewTimer);
      previewTimer = null;
    }
  };

  const handleSubmit = (): void => {
    if (ended || !current) return;
    const raw = input.value;
    const candidates = acceptedNamesFor(current);
    const result = fuzzyMatch({ input: raw, candidates, tolerance });
    const now = Date.now();
    if (result.matched) {
      streak += 1;
      streakEl.textContent = String(streak);
      appState = recordCorrect(appState, current.iso, now);
      saveState(appState);
      nextQuestion();
    } else {
      appState = recordWrong(appState, current.iso, now);
      saveState(appState);
      input.disabled = true;
      // Pause briefly on the missed country so the user can register
      // where it was before the end screen takes over.
      const missed = current;
      previewTimer = window.setTimeout(() => {
        endRun(missed);
      }, ESCAPE_PREVIEW_MS);
    }
  };

  const onSubmit = (e: SubmitEvent): void => {
    e.preventDefault();
    handleSubmit();
  };

  const onBack = (): void => ctx.nav.goArcade();
  const onRetry = (): void => ctx.nav.goArcadeMode({ mode: "survival", region });
  const onExit = (): void => ctx.nav.goArcade();
  const onLeaderboard = (): void =>
    ctx.nav.goLeaderboard({ mode: "survival", region });
  form.addEventListener("submit", onSubmit);
  backBtn.addEventListener("click", onBack);
  retryBtn.addEventListener("click", onRetry);
  leaderboardBtn.addEventListener("click", onLeaderboard);
  exitBtn.addEventListener("click", onExit);

  nextQuestion();

  return () => {
    destroyed = true;
    if (previewTimer !== null) window.clearTimeout(previewTimer);
    form.removeEventListener("submit", onSubmit);
    backBtn.removeEventListener("click", onBack);
    retryBtn.removeEventListener("click", onRetry);
    leaderboardBtn.removeEventListener("click", onLeaderboard);
    exitBtn.removeEventListener("click", onExit);
    ctx.globe.setTargetIso(null);
    ctx.globe.setHoverEnabled(true);
    root.classList.remove("view-arcade-run");
  };
};
